import React from 'react';
import { motion } from 'framer-motion';
import { Package, Clock, CheckCircle, XCircle, Truck, MapPin, CheckCircle2, Ban } from 'lucide-react';

const STATUS_STEPS = ['confirmed', 'processing', 'shipped', 'out_for_delivery', 'delivered'];
const STATUS_LABELS = {
  pending: 'Order Placed', confirmed: 'Confirmed', processing: 'Processing',
  shipped: 'Shipped', out_for_delivery: 'Out for Delivery', delivered: 'Delivered', cancelled: 'Cancelled'
};

function getStatusIcon(status) {
  switch (status) {
    case 'completed': return <CheckCircle className="h-4 w-4 text-green-600" />;
    case 'pending': return <Clock className="h-4 w-4 text-yellow-600" />;
    case 'failed': return <XCircle className="h-4 w-4 text-red-600" />;
    default: return <Clock className="h-4 w-4 text-gray-400" />;
  }
}

function DeliveryCountdown({ estimatedDate }) {
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const deliveryDate = new Date(estimatedDate); deliveryDate.setHours(0, 0, 0, 0);
  const diffDays = Math.ceil((deliveryDate - today) / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return <span className="text-lg font-bold text-green-600">Arriving Today!</span>;
  if (diffDays === 1) return <span className="text-lg font-bold text-blue-600">Arriving Tomorrow</span>;
  if (diffDays > 0) return <div><span className="text-2xl font-bold text-[#1a3a5c]">{diffDays}</span><span className="text-sm text-gray-600 ml-1">days left</span></div>;
  return <span className="text-sm text-orange-600">Delivery expected soon</span>;
}

function CancellationStatus({ order }) {
  const styleMap = {
    approved: { bg: 'bg-green-50 border-green-200', text: 'text-green-800', label: 'Cancellation Approved' },
    rejected: { bg: 'bg-red-50 border-red-200', text: 'text-red-800', label: 'Cancellation Rejected' },
    pending: { bg: 'bg-yellow-50 border-yellow-200', text: 'text-yellow-800', label: 'Cancellation Pending' },
  };
  const style = styleMap[order.cancellation_status] || styleMap.pending;
  const refundColor = order.refund_status === 'processed' ? 'text-green-700' : order.refund_status === 'failed' ? 'text-red-700' : 'text-yellow-700';

  return (
    <div className={`mt-4 p-4 rounded-lg border ${style.bg}`}>
      <div className="flex items-center justify-between mb-2">
        <span className={`font-medium ${style.text}`}>{style.label}</span>
      </div>
      <p className="text-sm text-gray-600">Reason: {order.cancellation_reason}</p>
      {order.cancellation_details && <p className="text-sm text-gray-500 mt-1">Details: {order.cancellation_details}</p>}
      {order.cancellation_admin_note && (
        <p className="text-sm text-gray-600 mt-2 pt-2 border-t border-gray-200"><strong>Response:</strong> {order.cancellation_admin_note}</p>
      )}
      {order.refund_status && order.refund_status !== 'not_applicable' && (
        <div className="mt-2 pt-2 border-t border-gray-200">
          <span className={`text-sm font-medium ${refundColor}`}>
            Refund: {order.refund_status === 'processed' ? `₹${order.refund_amount?.toFixed(2)} - Processed` :
                     order.refund_status === 'failed' ? 'Failed - Contact Support' :
                     `₹${order.refund_amount?.toFixed(2)} - Pending`}
          </span>
        </div>
      )}
    </div>
  );
}

function OrderCard({ order, canRequestCancellation, openCancelModal }) {
  const currentStatusIndex = STATUS_STEPS.indexOf(order.order_status);
  const isCancelled = order.order_status === 'cancelled';

  return (
    <motion.div key={order.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
      className="bg-white p-6 rounded-lg border border-gray-200" data-testid={`order-${order.id}`}>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-serif text-xl font-semibold text-[#1a3a5c]">Order #{order.tracking_id}</h3>
          <p className="text-sm text-gray-600">{new Date(order.created_at).toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' })}</p>
        </div>
        <div className="flex flex-col items-end gap-2">
          <div className="flex items-center space-x-2">
            {getStatusIcon(order.payment_status)}
            <span className="capitalize font-medium text-gray-700 text-sm">Payment: {order.payment_status}</span>
          </div>
          <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
            isCancelled ? 'bg-red-100 text-red-800' : order.order_status === 'delivered' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'
          }`}>
            {isCancelled ? <XCircle className="h-3 w-3 mr-1" /> : order.order_status === 'delivered' ? <CheckCircle2 className="h-3 w-3 mr-1" /> : <Truck className="h-3 w-3 mr-1" />}
            {STATUS_LABELS[order.order_status] || 'Pending'}
          </span>
        </div>
      </div>

      {order.estimated_delivery_date && !isCancelled && order.order_status !== 'delivered' && (
        <div className="mb-4 p-4 bg-gradient-to-r from-blue-50 to-green-50 border border-blue-200 rounded-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="bg-white p-2 rounded-full shadow-sm mr-3"><Truck className="h-5 w-5 text-[#1a3a5c]" /></div>
              <div>
                <p className="text-sm text-gray-600">Estimated Delivery</p>
                <p className="font-semibold text-[#1a3a5c]">{new Date(order.estimated_delivery_date).toLocaleDateString('en-IN', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p>
              </div>
            </div>
            <div className="text-right"><DeliveryCountdown estimatedDate={order.estimated_delivery_date} /></div>
          </div>
          {order.courier_name && (
            <div className="mt-2 pt-2 border-t border-blue-100 flex items-center justify-between">
              <span className="text-sm text-gray-600">Shipped via: <strong>{order.courier_name}</strong></span>
              {order.external_tracking_url && <a href={order.external_tracking_url} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:underline flex items-center">Track Package →</a>}
            </div>
          )}
        </div>
      )}

      {!isCancelled && order.order_status !== 'pending' && (
        <div className="mb-6 px-2">
          <div className="flex items-center justify-between relative">
            <div className="absolute left-0 right-0 top-4 h-0.5 bg-gray-200">
              <div className="h-full bg-[#1a3a5c] transition-all duration-500" style={{ width: `${Math.max(0, (currentStatusIndex / (STATUS_STEPS.length - 1)) * 100)}%` }} />
            </div>
            {STATUS_STEPS.map((status, index) => {
              const isCompleted = currentStatusIndex >= index;
              const isCurrent = currentStatusIndex === index;
              const StepIcon = status === 'confirmed' ? CheckCircle : status === 'processing' ? Package : status === 'shipped' ? Truck : status === 'out_for_delivery' ? MapPin : CheckCircle2;
              return (
                <div key={status} className="flex flex-col items-center relative z-10">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center transition-all ${isCompleted ? 'bg-[#1a3a5c] text-white' : 'bg-gray-200 text-gray-400'} ${isCurrent ? 'ring-2 ring-[#1a3a5c] ring-offset-2' : ''}`}>
                    <StepIcon className="h-4 w-4" />
                  </div>
                  <span className={`text-xs mt-2 text-center ${isCompleted ? 'text-[#1a3a5c] font-medium' : 'text-gray-400'}`}>{STATUS_LABELS[status]}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {isCancelled && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-center text-red-800"><XCircle className="h-5 w-5 mr-2" /><span className="font-medium">This order has been cancelled</span></div>
        </div>
      )}

      <div className="space-y-2 mb-4">
        {order.items.map((item, i) => (
          <div key={`${item.product_id}-${i}`} className="flex justify-between text-sm">
            <span className="text-gray-600">{item.product_name} x{item.quantity}</span>
            <span className="font-medium">₹{item.total_price?.toFixed(2) || '0.00'}</span>
          </div>
        ))}
      </div>

      {(order.promo_discount > 0 || order.points_discount > 0) && (
        <div className="bg-green-50 p-3 rounded mb-4 text-sm">
          {order.promo_discount > 0 && <div className="flex justify-between text-green-700"><span>Promo Code ({order.promo_code})</span><span>-₹{order.promo_discount.toFixed(2)}</span></div>}
          {order.points_discount > 0 && <div className="flex justify-between text-green-700"><span>Points Redeemed ({order.points_used} pts)</span><span>-₹{order.points_discount.toFixed(2)}</span></div>}
        </div>
      )}

      <div className="border-t border-gray-200 pt-3">
        <div className="flex justify-between">
          <span className="font-semibold text-[#1a3a5c]">Total Amount</span>
          <span className="font-bold text-[#1a3a5c] text-lg">₹{order.total_amount?.toFixed(2) || '0.00'}</span>
        </div>
        {order.points_earned > 0 && <p className="text-xs text-[#C9A967] mt-1 text-right">+{order.points_earned} reward points earned</p>}
      </div>

      {order.cancellation_requested && <CancellationStatus order={order} />}
      {canRequestCancellation(order) && (
        <button onClick={() => openCancelModal(order.id)} className="mt-4 w-full py-2 px-4 border border-red-200 text-red-600 rounded-lg hover:bg-red-50 transition-colors flex items-center justify-center" data-testid={`cancel-order-btn-${order.id}`}>
          <Ban className="h-4 w-4 mr-2" />Request Cancellation
        </button>
      )}
    </motion.div>
  );
}

export default function OrdersSection({ orders, cancelModal, setCancelModal, cancellationReasons, submitCancellationRequest, canRequestCancellation, openCancelModal }) {
  return (
    <>
      {orders.length === 0 ? (
        <div className="text-center py-24" data-testid="no-orders">
          <Package className="h-24 w-24 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-600 text-lg">You have not placed any orders yet</p>
        </div>
      ) : (
        <div className="space-y-6">
          {orders.map((order) => (
            <OrderCard key={order.id} order={order} canRequestCancellation={canRequestCancellation} openCancelModal={openCancelModal} />
          ))}
        </div>
      )}

      {cancelModal.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="bg-white rounded-lg max-w-md w-full p-6" data-testid="cancel-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-serif text-xl font-semibold text-[#1a3a5c]">Request Cancellation</h3>
              <button onClick={() => setCancelModal({ isOpen: false, orderId: null, reason: '', details: '' })} className="text-gray-400 hover:text-gray-600">×</button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Why do you want to cancel? *</label>
                <select value={cancelModal.reason} onChange={(e) => setCancelModal({...cancelModal, reason: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#1a3a5c]" data-testid="cancel-reason-select">
                  <option value="">Select a reason</option>
                  {cancellationReasons.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Additional details (optional)</label>
                <textarea value={cancelModal.details} onChange={(e) => setCancelModal({...cancelModal, details: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#1a3a5c]" rows={3} placeholder="Any additional information..." data-testid="cancel-details-input" />
              </div>
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <p className="text-sm text-yellow-800"><strong>Note:</strong> Cancellation requests are subject to review. Refund will be initiated after approval.</p>
              </div>
            </div>
            <div className="flex justify-end space-x-3 mt-6">
              <button onClick={() => setCancelModal({ isOpen: false, orderId: null, reason: '', details: '' })} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg">Keep Order</button>
              <button onClick={submitCancellationRequest} disabled={!cancelModal.reason} className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed" data-testid="submit-cancel-btn">Submit Request</button>
            </div>
          </motion.div>
        </div>
      )}
    </>
  );
}
